import * as React from 'react'
import Button from './button'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Stack } from 'components'
import { FormattedMessage } from 'react-intl'
import intl from 'localization/components'

interface ActionDialogProps {
  title?: React.ReactNode
  content: ((handleClose: () => void) => React.ReactNode) | React.ReactNode
  action?: () => void
  render: (handleOpen: () => void) => React.ReactNode
  removeActions?: boolean
  disabled?: boolean
  cancelText?: React.ReactNode
  agreeText?: React.ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  onClose?: () => void
}

const ActionDialog = (props: ActionDialogProps) => {
  const [open, setOpen] = React.useState(false)

  const {
    title,
    content,
    action,
    render,
    removeActions,
    disabled = false,
    cancelText = <FormattedMessage id={intl.confirmAction('cancel')} />,
    agreeText = <FormattedMessage id={intl.confirmAction('agree')} />,
    maxWidth,
    fullWidth = false,
    onClose,
  } = props

  const handleClickOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const handleAction = () => {
    handleClose()

    if (typeof action === 'function') {
      action()
    }
  }

  return (
    <>
      {render(handleClickOpen)}
      <Dialog
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 600,
            fontSize: '1.125rem',
          }}
          id="alert-dialog-title"
        >
          {title}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {typeof content === 'function' ? content(handleClose) : content}
        </DialogContent>
        {!removeActions ? (
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={1}>
              <Button
                data-cy="action-dialog-cancel"
                onClick={handleClose}
                variant="text"
              >
                {cancelText}
              </Button>
              <Button
                data-cy="action-dialog-agree"
                onClick={handleAction}
                color="primary"
                autoFocus
                disabled={disabled}
              >
                {agreeText}
              </Button>
            </Stack>
          </DialogActions>
        ) : null}
      </Dialog>
    </>
  )
}

export default ActionDialog
